import { httpService } from "../httpService.service";
import https from "../http";

// Backend contract — TRACK 2 AI layer (AI Assistant), base /api/AIAssistant.
// All AI output is human-in-the-loop: endpoints return an AIDraft that a human
// reviews then commits or discards. Nothing is applied until committed.
//
// AIDraftResponse: { id, type, status, entityId, entityType, projectId,
//   contractId, title, content, score?, committedDate?, committedBy?, createdDate }.
const BASE = "/api/AIAssistant";

// AIDraft.type
export const AIDraftType = {
  1: "QualityScore",
  2: "SmartTasks",
  3: "SitRep",
  4: "IPNarrative",
  5: "DonorNarrative",
  6: "PatternAlert",
};

// AIDraft.status
export const AIDraftStatus = {
  1: "Draft",
  2: "Committed",
  3: "Discarded",
};

// F-12 — quality scoring. content JSON { score:1-5, summary, issues:[{severity,message}] }.
export const scoreSiteVisit = (siteVisitId, callback, errCallback) =>
  httpService.post(
    `${BASE}/scoreSiteVisit?siteVisitId=${siteVisitId}`,
    {},
    callback,
    errCallback
  );

// F-13 — smart tasks. content JSON array [{ name, type, description, deadline }].
export const generateTasks = (siteVisitId, callback, errCallback) =>
  httpService.post(
    `${BASE}/generateTasks?siteVisitId=${siteVisitId}`,
    {},
    callback,
    errCallback
  );

// F-14 — natural-language query. Body { projectId, question } →
// { entity, interpretation, count, results:[{ id, title, status, type }] }.
// Results are already RBAC-scoped server-side.
export const query = (projectId, question, callback, errCallback) =>
  httpService.post(
    `${BASE}/query`,
    { projectId, question },
    callback,
    errCallback
  );

// ── Conversational chat ──────────────────────────────────────────────────────
// Persisted, multi-turn variant of `query`. The server keeps a conversation so
// follow-up questions have context. Send `conversationId` empty/null to start a
// new conversation; the response returns the id to reuse for follow-ups.
//
// ChatSendRequest { conversationId, projectId, message } →
// ChatSendResponse { conversationId, answer,
//   data: { entity, interpretation, count, results:[{ id, title, status, type }] } }.
export const sendChat = (
  { conversationId, projectId, message } = {},
  callback,
  errCallback
) =>
  httpService.post(
    `${BASE}/chat`,
    { conversationId: conversationId || null, projectId, message },
    callback,
    errCallback
  );

// List a project's conversations (newest first, server-ordered).
// → [{ id, title, projectId, createdDate, updatedDate }].
export const getChatConversations = (projectId, callback, errCallback) =>
  httpService.get(
    `${BASE}/chatConversations?projectId=${projectId}`,
    callback,
    errCallback
  );

// One conversation with its full message history.
// → { id, title, projectId, createdDate, updatedDate,
//     messages:[{ id, role, content, createdDate, data? }] }.
export const getChatConversation = (conversationId, callback, errCallback) =>
  httpService.get(
    `${BASE}/chatConversation?conversationId=${conversationId}`,
    callback,
    errCallback
  );

// Delete a conversation.
export const deleteChatConversation = (conversationId, callback, errCallback) =>
  httpService.post(
    `${BASE}/deleteChatConversation?conversationId=${conversationId}`,
    {},
    callback,
    errCallback
  );

// F-15 — SitRep. Body { projectId, from?, to? } → AIDraft (content = markdown).
export const generateSitRep = (
  { projectId, from, to } = {},
  callback,
  errCallback
) =>
  httpService.post(
    `${BASE}/generateSitRep`,
    { projectId, from, to },
    callback,
    errCallback
  );

// F-17 — IP / donor narratives (batch). Body { iPPlanIds:[], donorFacing:bool }
// → List<AIDraft> (markdown).
export const generateIPNarratives = (
  ipPlanIds,
  donorFacing,
  callback,
  errCallback
) =>
  httpService.post(
    `${BASE}/generateIPNarratives`,
    { ipPlanIds, donorFacing },
    callback,
    errCallback
  );

// F-18 — pattern alerts. Also runs daily server-side. Surfaces PatternAlert drafts.
export const detectRedFlagPatterns = (callback, errCallback) =>
  httpService.post(`${BASE}/detectRedFlagPatterns`, {}, callback, errCallback);

// F-19 — donor report export → downloads an .xlsx. Returns the axios promise
// resolving to a Blob (responseType: "blob"), since this is a binary file and
// not the JSON envelope. Caller saves it (e.g. with file-saver).
export const exportDonorReport = (projectId) =>
  https.get(`${BASE}/exportDonorReport?projectId=${projectId}`, {
    responseType: "blob",
  });

// Draft management — unified "AI Drafts" review inbox.
export const getDrafts = (
  { entityId, projectId, type, status, pageNumber = 1, pageSize = 20 } = {},
  callback,
  errCallback
) => {
  const params = new URLSearchParams();
  if (entityId) params.append("EntityId", entityId);
  if (projectId) params.append("ProjectId", projectId);
  if (type !== undefined && type !== null && type !== "")
    params.append("Type", type);
  if (status !== undefined && status !== null && status !== "")
    params.append("Status", status);
  params.append("pageNumber", pageNumber);
  params.append("pageSize", pageSize);
  return httpService.get(
    `${BASE}/getDrafts?${params.toString()}`,
    callback,
    errCallback
  );
};

// Review/edit then commit. Body { id, editedContent? }.
export const commitDraft = (id, editedContent, callback, errCallback) =>
  httpService.post(
    `${BASE}/commitDraft`,
    { id, editedContent },
    callback,
    errCallback
  );

// Discard a draft.
export const discardDraft = (id, callback, errCallback) =>
  httpService.post(`${BASE}/discardDraft?id=${id}`, {}, callback, errCallback);
